import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Download, Award } from 'lucide-react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

const STAGE_LABELS = {
  Strong: 'Excellent',
  Flourishing: 'Very Good',
  Developing: 'Good',
  Growing: 'Progressing',
  Sprouting: 'Emerging',
};

export default function CognitivePassportPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const certRef = useRef(null);
  const wrapperRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studentName, setStudentName] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/assessment/cognitive-passport');
        setData(res.data);
      } catch {
        toast.error('Failed to load assessment data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  // Scale certificate to fit screen width
  useEffect(() => {
    const updateScale = () => {
      if (wrapperRef.current) {
        const containerWidth = wrapperRef.current.offsetWidth - 16;
        const certWidth = 800;
        setScale(Math.min(1, containerWidth / certWidth));
      }
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [loading]);

  const handleDownload = async () => {
    if (!studentName.trim()) {
      toast.error('Please enter the student\'s full name.');
      return;
    }
    setDownloading(true);
    try {
      const canvas = await html2canvas(certRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
      });
      const link = document.createElement('a');
      link.download = `BrainGym-Jr-Cognitive-Passport-${studentName.trim().replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Certificate downloaded!');
    } catch {
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Award className="w-12 h-12 text-amber-500 mx-auto mb-3 animate-pulse" />
          <p className="text-slate-600">Preparing your Cognitive Passport...</p>
        </div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cognitive-passport-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-slate-100 rounded-full" data-testid="passport-back-btn">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <h1 className="text-lg font-bold text-slate-900 font-fredoka">Cognitive Passport</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Name Input */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5" data-testid="name-input-card">
          <label className="block text-sm font-semibold text-slate-700 mb-2">Student's Full Name</label>
          <input
            type="text"
            value={studentName}
            onChange={(e) => setStudentName(e.target.value)}
            placeholder="e.g. Sarah Johnson"
            className="w-full px-4 py-3 border border-slate-300 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
            data-testid="student-name-input"
          />
          <p className="text-xs text-slate-400 mt-2">This name will appear on the certificate. It is not stored.</p>
        </div>

        {/* Certificate Preview */}
        <div ref={wrapperRef} className="overflow-hidden">
          <div style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            height: scale < 1 ? `${scale * 750}px` : 'auto',
          }}>
            <div ref={certRef} style={{ width: 800, fontFamily: "'Georgia', 'Times New Roman', serif" }} data-testid="certificate-preview">
            {/* Certificate Container */}
            <div style={{
              background: '#ffffff',
              border: '3px solid #D4AF37',
              borderRadius: 12,
              padding: 48,
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Inner border */}
              <div style={{
                position: 'absolute',
                top: 10,
                left: 10,
                right: 10,
                bottom: 10,
                border: '1px solid #D4AF3766',
                borderRadius: 8,
                pointerEvents: 'none',
              }} />

              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: 32 }}>
                <div style={{ fontSize: 13, letterSpacing: 4, color: '#94A3B8', textTransform: 'uppercase', marginBottom: 8 }}>
                  Certificate of Achievement
                </div>
                <div style={{ fontSize: 32, fontWeight: 700, color: '#0F172A', letterSpacing: 1 }}>
                  Cognitive Passport
                </div>
                <div style={{ fontSize: 14, color: '#64748B', marginTop: 6, fontFamily: "'Fredoka', sans-serif" }}>
                  BrainGym Jr &mdash; End-of-Term Progress Report
                </div>
              </div>

              {/* Divider */}
              <div style={{ height: 1, background: 'linear-gradient(to right, transparent, #D4AF37, transparent)', marginBottom: 28 }} />

              {/* Student Name */}
              <div style={{ textAlign: 'center', marginBottom: 28 }}>
                <div style={{ fontSize: 12, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 }}>
                  Awarded to
                </div>
                <div style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: '#0F172A',
                  borderBottom: '2px solid #D4AF37',
                  display: 'inline-block',
                  paddingBottom: 4,
                  minWidth: 300,
                  minHeight: 36,
                }}>
                  {studentName.trim() || 'Student Name'}
                </div>
                {data?.age && (
                  <div style={{ fontSize: 13, color: '#94A3B8', marginTop: 6 }}>
                    Age {data.age} &middot; Full 12 weeks completed
                  </div>
                )}
              </div>

              {/* Skills Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 32 }}>
                {data?.skills?.map((skill) => (
                  <div key={skill.id} style={{
                    border: '1px solid #E2E8F0',
                    borderRadius: 10,
                    padding: '16px 18px',
                    background: '#FAFAFA',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1E293B' }}>
                        {skill.name}
                      </div>
                      <div style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: skill.color,
                        background: `${skill.color}18`,
                        padding: '2px 10px',
                        borderRadius: 20,
                      }}>
                        {STAGE_LABELS[skill.stage] || skill.stage}
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div style={{ height: 8, background: '#E2E8F0', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${skill.score}%`,
                        background: skill.color,
                        borderRadius: 99,
                        transition: 'width 0.6s ease',
                      }} />
                    </div>
                    <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>
                      {skill.description}
                    </div>
                  </div>
                ))}
              </div>

              {/* Gold Stamp + Signature Row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 16 }}>
                {/* Date */}
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                    Date Issued
                  </div>
                  <div style={{ fontSize: 14, color: '#334155', fontWeight: 600 }}>
                    {today}
                  </div>
                </div>

                {/* Gold Stamp */}
                <div style={{
                  width: 110,
                  height: 110,
                }}>
                  <img
                    src="/excellence-seal.png"
                    alt="Seal of Excellence"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    crossOrigin="anonymous"
                  />
                </div>

                {/* Signature */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                    Authorized by
                  </div>
                  <div style={{ fontSize: 14, color: '#334155', fontWeight: 600, fontStyle: 'italic', fontFamily: "'Fredoka', sans-serif" }}>
                    BrainGym Jr Team
                  </div>
                </div>
              </div>

            </div>
          </div>
          </div>
        </div>

        {/* Download Button */}
        <div className="flex justify-center pb-8">
          <button
            onClick={handleDownload}
            disabled={downloading || !studentName.trim()}
            className="flex items-center gap-2 px-8 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="download-passport-btn"
          >
            {downloading ? (
              <>Generating...</>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Download Certificate
              </>
            )}
          </button>
        </div>
      </main>
    </div>
  );
}
